import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import {
  AgentApprovalRequestRecord,
  AgencyRecord,
  AgencyUpsert,
  AgentRecord,
  ApiRepository,
  AuthCredentials,
  AuthUser,
  PosRecord,
  PosUpsertPayload,
  SupervisorApprovalRequestRecord,
  SupervisorRecord,
  SupervisorUpsertPayload,
  TaskRecord
} from './api-repository';
import { toAgencyRecord } from '../../../features/agencies/agencies.types';
import { toAgentRecord } from '../../../features/agents/agents.types';
import { toPosRecord } from '../../../features/pos/pos.types';
import { toSupervisorRecord } from '../../../features/supervisors/supervisors.types';
import { buildReviewRows, cycleValue, formatDisplayReviewDate, formatIsoReviewDate, formatSequence } from '../../../shared/utils';

interface AuthMockFile {
  defaultOtp: string;
  users: Array<AuthUser & { password: string }>;
}

interface SupervisorsMockFile {
  approved: SupervisorRecord[];
  pending: SupervisorApprovalRequestRecord[];
}

@Injectable({ providedIn: 'root' })
export class MockRepository extends ApiRepository {
  private readonly http = inject(HttpClient);
  private readonly sessionOtp = new Map<string, string>();
  private supervisorsCache: SupervisorsMockFile | null = null;
  private agenciesCache: AgencyRecord[] | null = null;
  private agentsCache: AgentRecord[] | null = null;
  private posCache: PosRecord[] | null = null;
  private pendingAgentRequestsCache: AgentApprovalRequestRecord[] | null = null;
  private tasksCache: TaskRecord[] | null = null;

  override async login(payload: AuthCredentials): Promise<AuthUser | null> {
    const auth = await this.readAuthMock();
    const match = auth.users.find((item) => item.email === payload.email && item.password === payload.password);

    if (!match) {
      return null;
    }

    return {
      email: match.email,
      role: match.role,
      name: match.name,
      firstName: match.firstName ?? match.name.split(' ')[0] ?? match.name,
      lastName: match.lastName ?? match.name.split(' ').slice(1).join(' '),
      phone: match.phone ?? '+237 677 112 233',
      address: match.address ?? 'Bonapriso, Douala, Cameroon',
      avatar: match.avatar ?? '',
      language: match.language ?? 'en',
      roles: match.roles ?? ['Admin', 'Inventory Manager', 'Finance Admin'],
      customRbacRoles: match.customRbacRoles ?? ['Agency Operations Review', 'Central Stock Approval'],
      status: match.status ?? 'active',
      userType: match.userType ?? (match.role === 'admin' ? 'Platform Admin' : 'Field Supervisor'),
      agency: match.agency ?? 'Palladium Agency Douala Central',
      supervisor: match.supervisor ?? 'Regional Director - Francophone Cluster'
    };
  }

  override requestSignup(_: AuthCredentials & { fullName: string }): Promise<void> {
    return Promise.resolve();
  }

  override async requestPasswordOtp(email: string): Promise<boolean> {
    const auth = await this.readAuthMock();
    const hasUser = auth.users.some((item) => item.email === email);

    if (!hasUser) {
      return false;
    }

    this.sessionOtp.set(email, auth.defaultOtp);
    return true;
  }

  override verifyPasswordOtp(email: string, otp: string): Promise<boolean> {
    return Promise.resolve(this.sessionOtp.get(email) === otp);
  }

  override resetPassword(email: string, _: string): Promise<boolean> {
    this.sessionOtp.delete(email);
    return Promise.resolve(Boolean(email));
  }

  override async getAgencies(): Promise<AgencyRecord[]> {
    return this.readAgenciesMock();
  }

  override async getAgencyById(id: string): Promise<AgencyRecord | null> {
    const agencies = await this.readAgenciesMock();
    return agencies.find((item) => item.id === id) ?? null;
  }

  override async createAgency(payload: AgencyUpsert): Promise<AgencyRecord> {
    const agencies = await this.readAgenciesMock();
    const created = toAgencyRecord(`ag-${Math.random().toString(16).slice(2, 6)}`, payload);
    this.agenciesCache = [created, ...agencies];
    return created;
  }

  override async updateAgency(id: string, payload: AgencyUpsert): Promise<AgencyRecord | null> {
    const current = await this.getAgencyById(id);

    if (!current) {
      return null;
    }

    const updated = toAgencyRecord(id, payload, current);
    const agencies = await this.readAgenciesMock();
    this.agenciesCache = agencies.map((item) => (item.id === id ? updated : item));
    return updated;
  }

  override async getAgents(): Promise<AgentRecord[]> {
    return this.readAgentsMock();
  }

  override async getAgentById(id: string): Promise<AgentRecord | null> {
    const agents = await this.getAgents();
    return agents.find((item) => item.id === id) ?? null;
  }

  override async getPendingAgentRequests(): Promise<AgentApprovalRequestRecord[]> {
    return this.readPendingAgentRequestsMock();
  }

  override async approveAgentRequest(id: string): Promise<AgentRecord | null> {
    const requests = await this.readPendingAgentRequestsMock();
    const request = requests.find((item) => item.id === id);

    if (!request) {
      return null;
    }

    const approved = toAgentRecord(`agt-approved-${id.replace('agt-req-', '')}`, {
      name: request.name,
      type: request.type,
      status: 'active',
      dealerId: request.dealerId,
      joinedAt: request.requestDate.split('T')[0] ?? formatIsoReviewDate(1),
      contact: request.contact,
      address: request.address,
      assignedAgencies: [request.requestedAgency],
      assignedPos: request.requestedPos
    });

    const agents = await this.readAgentsMock();
    this.agentsCache = [approved, ...agents];
    this.pendingAgentRequestsCache = requests.filter((item) => item.id !== id);
    return approved;
  }

  override async getPos(): Promise<PosRecord[]> {
    return this.readPosMock();
  }

  override async getPosById(id: string): Promise<PosRecord | null> {
    const records = await this.readPosMock();
    return records.find((item) => item.id === id) ?? null;
  }

  override async createPos(payload: PosUpsertPayload): Promise<PosRecord> {
    const records = await this.readPosMock();
    const created = toPosRecord(`pos-${Math.random().toString(16).slice(2, 8)}`, payload);
    this.posCache = [created, ...records];
    return created;
  }

  override async updatePos(id: string, payload: PosUpsertPayload): Promise<PosRecord | null> {
    const current = await this.getPosById(id);

    if (!current) {
      return null;
    }

    const updated = toPosRecord(id, payload, current);
    const records = await this.readPosMock();
    this.posCache = records.map((item) => (item.id === id ? updated : item));
    return updated;
  }

  override async deletePos(id: string): Promise<boolean> {
    const records = await this.readPosMock();
    const next = records.filter((item) => item.id !== id);
    const changed = next.length !== records.length;
    this.posCache = next;
    return changed;
  }

  override async getSupervisors(): Promise<SupervisorRecord[]> {
    const supervisors = await this.readSupervisorsMock();
    return [...supervisors.approved];
  }

  override async getPendingSupervisorRequests(): Promise<SupervisorApprovalRequestRecord[]> {
    const supervisors = await this.readSupervisorsMock();
    return [...supervisors.pending];
  }

  override async createSupervisor(payload: SupervisorUpsertPayload): Promise<SupervisorRecord> {
    const supervisors = await this.readSupervisorsMock();
    const record = toSupervisorRecord(`sup-${Math.random().toString(16).slice(2, 6)}`, payload);
    this.supervisorsCache = {
      approved: [record, ...supervisors.approved],
      pending: supervisors.pending
    };
    return record;
  }

  override async updateSupervisor(id: string, payload: SupervisorUpsertPayload): Promise<SupervisorRecord | null> {
    const supervisors = await this.readSupervisorsMock();
    const current = supervisors.approved.find((item) => item.id === id);

    if (!current) {
      return null;
    }

    const updated = toSupervisorRecord(id, payload, current);
    this.supervisorsCache = {
      approved: supervisors.approved.map((item) => (item.id === id ? updated : item)),
      pending: supervisors.pending
    };
    return updated;
  }

  override async approveSupervisorRequest(id: string): Promise<SupervisorRecord | null> {
    const supervisors = await this.readSupervisorsMock();
    const request = supervisors.pending.find((item) => item.id === id);

    if (!request) {
      return null;
    }

    const approved = toSupervisorRecord(`sup-${id.replace('sup-req-', '')}`, {
      name: request.name,
      code: `SUP-${request.id.slice(-3).toUpperCase()}`,
      status: 'active',
      contact: request.contact,
      address: {
        formattedAddress: '',
        streetAddress: '',
        locality: request.requestedAgency.city ?? '',
        subLocality: request.requestedAgency.area ?? '',
        region: '',
        country: '',
        postalCode: '',
        area: request.requestedAgency.area ?? '',
        geo: [0, 0]
      },
      assignedAgencies: [request.requestedAgency]
    });

    this.supervisorsCache = {
      approved: [approved, ...supervisors.approved],
      pending: supervisors.pending.filter((item) => item.id !== id)
    };

    return approved;
  }

  override async deleteSupervisor(id: string): Promise<boolean> {
    const supervisors = await this.readSupervisorsMock();
    const next = supervisors.approved.filter((item) => item.id !== id);

    if (next.length === supervisors.approved.length) {
      return false;
    }

    this.supervisorsCache = {
      approved: next,
      pending: supervisors.pending
    };

    return true;
  }

  override async getTasks(): Promise<TaskRecord[]> {
    return this.readTasksMock();
  }

  private async readAuthMock(): Promise<AuthMockFile> {
    return firstValueFrom(this.http.get<AuthMockFile>('assets/mock-data/auth.mock.json'));
  }

  private async readAgenciesMock(): Promise<AgencyRecord[]> {
    if (this.agenciesCache) {
      return this.agenciesCache;
    }

    const rows = buildReviewRows(11, (index) => ({
      id: `ag-${index + 1}`,
      name: `Palladium Agency ${formatSequence(index + 1)}`,
      type: cycleValue(['Franchise', 'Direct'] as const, index),
      status: cycleValue(['active', 'inactive', 'pending'] as const, index),
      region: cycleValue(['Littoral', 'Centre', 'South-West'] as const, index),
      city: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
      area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
      code: `PA-${100 + index}`,
      manager: cycleValue(['Amina Diallo', 'Kofi Mensah', 'Nadine Tamo'] as const, index),
      phone: `+237 67${index}${index + 2} ${index + 3}${index + 4}${index + 5}`,
      email: `agency${index + 1}@palladium.africa`,
      employees: 12 + (index % 5),
      createdAt: formatIsoReviewDate(index + 1),
      updatedAt: formatIsoReviewDate(index),
      address: {
        formattedAddress: `${cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)}, ${cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index)}`,
        streetAddress: 'Main Street',
        locality: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        subLocality: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        region: cycleValue(['Littoral', 'Centre', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      }
    }));

    this.agenciesCache = rows.map((row) => toAgencyRecord(row.id, row as any));
    return this.agenciesCache;
  }

  private async readSupervisorsMock(): Promise<SupervisorsMockFile> {
    if (this.supervisorsCache) {
      return this.supervisorsCache;
    }

    const approved = buildReviewRows(9, (index) => ({
      id: `sup-${index + 1}`,
      name: cycleValue(['Kofi Mensah', 'Nadine Tamo', 'Yao Konate'] as const, index),
      code: `SUP-${100 + index}`,
      status: cycleValue(['active', 'inactive'] as const, index),
      contact: {
        email: `supervisor${index + 1}@palladium.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `67${index}${index + 2}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}`
        }
      },
      address: {
        formattedAddress: `${cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)}, ${cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index)}`,
        streetAddress: 'Main Street',
        locality: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        subLocality: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        region: cycleValue(['Littoral', 'Centre', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      },
      assignedAgencies: []
    })).map((row) => toSupervisorRecord(row.id, row as any));

    const pending = buildReviewRows(3, (index) => ({
      id: `sup-req-${index + 1}`,
      name: cycleValue(['Eric Mvoula', 'Clarisse Niba', 'Fabrice Simo'] as const, index),
      dealerId: `DLR-${400 + index}`,
      requestDate: formatDisplayReviewDate(index + 1),
      contact: {
        email: `candidate${index + 1}@palladium.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `66${index}${index + 2}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}`
        }
      },
      requestedAgency: {
        id: `ag-${index + 1}`,
        name: `Palladium Agency ${formatSequence(index + 1)}`,
        city: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)
      }
    }));

    this.supervisorsCache = {
      approved,
      pending: pending as any
    };

    return this.supervisorsCache as SupervisorsMockFile;
  }

  private async readAgentsMock(): Promise<AgentRecord[]> {
    if (this.agentsCache) {
      return this.agentsCache;
    }

    const rows = buildReviewRows(10, (index) => ({
      id: `agt-${index + 1}`,
      name: cycleValue(['Eli Tchoumi', 'Grace Nfor', 'Ismael Kone'] as const, index),
      type: cycleValue(['BTL', 'Sales', 'Support'] as const, index),
      status: cycleValue(['active', 'inactive', 'pending'] as const, index),
      dealerId: `DLR-${600 + index}`,
      joinedAt: formatIsoReviewDate(index + 2),
      contact: {
        email: `agent${index + 1}@palladium.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `65${index}${index + 2}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}`
        }
      },
      address: {
        formattedAddress: `${cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)}, ${cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index)}`,
        streetAddress: 'Main Street',
        locality: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        subLocality: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        region: cycleValue(['Littoral', 'Centre', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      },
      assignedAgencies: [],
      assignedPos: []
    }));

    this.agentsCache = rows.map((row) => toAgentRecord(row.id, row as any));
    return this.agentsCache;
  }

  private async readPendingAgentRequestsMock(): Promise<AgentApprovalRequestRecord[]> {
    if (this.pendingAgentRequestsCache) {
      return this.pendingAgentRequestsCache;
    }

    this.pendingAgentRequestsCache = buildReviewRows(4, (index) => ({
      id: `agt-req-${index + 1}`,
      name: cycleValue(['Serge Ewane', 'Linda Fotso', 'Pauline Ebong'] as const, index),
      type: cycleValue(['BTL', 'Sales'] as const, index),
      dealerId: `DLR-${700 + index}`,
      requestDate: formatIsoReviewDate(index + 2),
      contact: {
        email: `candidate-agent${index + 1}@palladium.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `64${index}${index + 2}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}`
        }
      },
      address: {
        formattedAddress: `${cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)}, ${cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index)}`,
        streetAddress: 'Main Street',
        locality: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        subLocality: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        region: cycleValue(['Littoral', 'Centre', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      },
      requestedAgency: {
        id: `ag-${index + 1}`,
        name: `Palladium Agency ${formatSequence(index + 1)}`,
        city: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)
      },
      requestedPos: []
    })) as any;

    return this.pendingAgentRequestsCache as AgentApprovalRequestRecord[];
  }

  private async readPosMock(): Promise<PosRecord[]> {
    if (this.posCache) {
      return this.posCache;
    }

    const seedRows: Array<[string, string, 'HV POS' | 'Standard POS' | 'Dealer POS', 'active' | 'inactive', string, string, string, string, number, number, 'ok' | 'low' | 'out-of-stock', string]> = [
      ['pos-1', 'Akwa Express POS', 'HV POS', 'active', 'Douala', 'Akwa', 'Mireille Tamo', 'Agency Douala Central', 1240, 400, 'ok', '2026-04-30'],
      ['pos-2', 'Bastos Market POS', 'Standard POS', 'active', 'Yaounde', 'Bastos', 'Eric Mvondo', 'Agency Yaounde East', 320, 250, 'ok', '2026-04-29'],
      ['pos-3', 'Bonaberi Dealer Point', 'Dealer POS', 'active', 'Douala', 'Bonaberi', 'Aline Ngono', 'Agency Bonaberi', 185, 220, 'low', '2026-04-28'],
      ['pos-4', 'Molyko Campus POS', 'HV POS', 'active', 'Buea', 'Molyko', 'Patrick Ewane', 'Agency Buea', 980, 350, 'ok', '2026-04-27'],
      ['pos-5', 'Limbe Mile Four POS', 'Standard POS', 'inactive', 'Limbe', 'Mile Four', 'Grace Nsom', 'Agency Limbe', 0, 180, 'out-of-stock', '2026-04-20'],
      ['pos-6', 'Bafoussam Centre POS', 'Dealer POS', 'active', 'Bafoussam', 'Centre Ville', 'Jean Kuete', 'Agency Bafoussam', 410, 300, 'ok', '2026-04-30'],
      ['pos-7', 'Deido Junction POS', 'Standard POS', 'active', 'Douala', 'Deido', 'Nadia Essomba', 'Agency Douala Central', 94, 200, 'low', '2026-04-26'],
      ['pos-8', 'Melen Float POS', 'HV POS', 'active', 'Yaounde', 'Melen', 'Samuel Owona', 'Agency Yaounde West', 1510, 500, 'ok', '2026-04-29'],
      ['pos-9', 'Nkongsamba Trade POS', 'Dealer POS', 'inactive', 'Nkongsamba', 'Commercial Avenue', 'Brenda Fokam', 'Agency Nkongsamba', 38, 150, 'low', '2026-04-18'],
      ['pos-10', 'Bonapriso Premium POS', 'HV POS', 'active', 'Douala', 'Bonapriso', 'Cedric Manga', 'Agency Douala Central', 1760, 650, 'ok', '2026-05-01'],
      ['pos-11', 'Etoudi Neighborhood POS', 'Standard POS', 'active', 'Yaounde', 'Etoudi', 'Marthe Abena', 'Agency Yaounde North', 260, 240, 'ok', '2026-04-25'],
      ['pos-12', 'Kumba Main Road POS', 'Dealer POS', 'active', 'Kumba', 'Main Road', 'Frank Besong', 'Agency Kumba', 72, 180, 'low', '2026-04-24']
    ];
    const rows = seedRows.map(([id, name, profile, status, city, area, agent, agency, stockOnHand, reorderLevel, stockStatus, lastStockUpdate], index) => ({
      id,
      name,
      profile,
      status,
      contact: {
        email: `${String(name).toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/\.$/, '')}@external-pos.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `67${index + 1}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}${index + 8}`
        }
      },
      address: {
        formattedAddress: `${area}, ${city}`,
        streetAddress: `${area} Main Road`,
        locality: city,
        subLocality: area,
        region: cycleValue(['Littoral', 'Centre', 'Southwest', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area,
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      },
      stockDetails: {
        stockOnHand: Number(stockOnHand),
        reorderLevel: Number(reorderLevel),
        stockStatus,
        lastStockUpdate,
        notes: `External POS managed by ${agent} through ${agency}. POS does not use the app in phase 1.`,
        entries: [
          {
            id: `${id}-entry-1`,
            dateTime: `${lastStockUpdate} 09:30`,
            agent,
            productAsset: 'MTN 4G SIM',
            quantity: Math.max(10, Math.round(Number(stockOnHand) / 4)),
            entryStatus: 'Verified',
            remarks: 'Agent-entered stock count',
            proofRef: `${String(id).toUpperCase()}-PROOF`,
            balanceAfter: Number(stockOnHand)
          }
        ]
      }
    }));

    const posRows: PosRecord[] = rows.map((row, index) => ({
      ...toPosRecord(row.id, row as any),
      code: `POS-${String(index + 1).padStart(4, '0')}`,
      primaryAgent: String(row.stockDetails.notes).match(/managed by (.*?) through/)?.[1] ?? 'Not assigned',
      primaryAgency: String(row.stockDetails.notes).match(/through (.*?)\./)?.[1] ?? 'Not assigned',
      lastVisitDate: row.stockDetails.lastStockUpdate
    }));
    this.posCache = posRows;
    return this.posCache;
  }

  private async readTasksMock(): Promise<TaskRecord[]> {
    if (this.tasksCache) {
      return this.tasksCache;
    }

    this.tasksCache = buildReviewRows(8, (index) => ({
      id: `tsk-${index + 1}`,
      title: `Task ${formatSequence(index + 1)}`,
      status: cycleValue(['open', 'in-progress', 'completed'] as const, index),
      dueDate: formatIsoReviewDate(index + 3)
    })) as any;

    return this.tasksCache as TaskRecord[];
  }
}
